import { useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Context, AppContext } from '@/core/app-context/export';
import { RemoveWhenHiddenDiv, HelpButton } from '@/shared/components/export';
import TabWidgetHost from '@/app/tabs/tab-widget-host';
import SingleWidgetHost from '@/app/dashboard/components/single-widget-host';
import { tabsWidgetRegistry } from '@/core/widgets/export';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import { useOpenWidget } from '@/core/widget-navigation/export';
import { HeadlineViewProvider } from '@/core/preferences/export';
import TabsModal from '@/app/tabs/tabs-modal';
import { authService, useSessionId } from '@/core/auth/export';
import { analyticsService, AnalyticsEvent } from '@/shared/analytics/export';
import DashboardNotifications from '@/app/dashboard/components/dashboard-notifications';
import { useWidgetNotifications, NotificationBell } from '@/core/notifications/export';
import { RequirePermissions, Permissions } from '@/core/permissions/export';

export default function Tabs() {
  const context = useContext<AppContext>(Context);
  const { notificationSoundOn, setNotificationSoundOn } = useWidgetNotifications();
  const openWidget = useOpenWidget();
  const [showTabsModal, setShowTabsModal] = useState(false);
  const [loginEventSent, setLoginEventSent] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const sessionId = useSessionId();

  useEffect(() => {
    const state: any = location.state;
    if (state && state.showBbgModal) {
      setShowTabsModal(true);
      navigate('.', { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (loginEventSent || !sessionId) return;

    const subject = authService.getSubjectData();
    if (subject && subject.provider && subject.provider.toLowerCase().includes('bbg')) {
      try {
        analyticsService.recordAnalyticsEvent(AnalyticsEvent.LOGIN, {
          subject,
          object: { type: 'bbg_login' },
          session: { id: sessionId },
        });
        setLoginEventSent(true);
      } catch (e) {
        console.warn('Failed to send BBG login analytics event', e);
      }
    }
  }, [sessionId, loginEventSent]);

  return (
    <>
      <Context.Provider value={{ showMenu: false, localPreferences: context.localPreferences }}>
        <RemoveWhenHiddenDiv className={'vh-100 d-block d-md-none'}>
          <SingleWidgetHost
            headerControls={
              <>
                <DashboardNotifications
                  notificationSoundOn={notificationSoundOn}
                  setNotificationSoundOn={setNotificationSoundOn}
                />
                {process.env.REACT_APP_TEST_ENV === 'true' ? (
                  <NotificationBell />
                ) : (
                  <RequirePermissions allow={Permissions.NOTIFICATIONS}>
                    <NotificationBell />
                  </RequirePermissions>
                )}
                <button
                  style={{ fontFamily: 'Inter' }}
                  className={'btn btn-sm ms-2 w-100 btn-outline-secondary'}
                  onClick={() => openWidget('settings')}
                >
                  <FontAwesomeIcon icon={faGear} />
                </button>
                <HelpButton
                  style={{ fontFamily: 'Inter' }}
                  className={'btn btn-sm ms-2 w-100 btn-outline-secondary'}
                />
              </>
            }
            registry={tabsWidgetRegistry}
            context={'tab'}
            includeDynamicWidgets
          />
        </RemoveWhenHiddenDiv>

        <RemoveWhenHiddenDiv className={'vh-100 d-none d-md-block'}>
          <HeadlineViewProvider>
            <TabWidgetHost
              notificationSoundOn={notificationSoundOn}
              setNotificationSoundOn={setNotificationSoundOn}
            />
          </HeadlineViewProvider>
        </RemoveWhenHiddenDiv>

        <TabsModal show={showTabsModal} />
      </Context.Provider>
    </>
  );
}
